import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getCryoList } from "./api";

export default function CryoQrPrint() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const macFromQs = sp.get("mac") || "";

  const [device, setDevice] = useState(null);
  const [me, setMe] = useState(null);

  // URL pagina inserimento manuale
  const url = useMemo(() => {
    const origin = window.location.origin;
    const qs = macFromQs ? `?mac=${encodeURIComponent(macFromQs)}` : "";
    return `${origin}/frighi/manual/${id}${qs}`;
  }, [id, macFromQs]);

  // QR esterno (PNG)
  const qrUrl = useMemo(() => {
    return (
      "https://api.qrserver.com/v1/create-qr-code/?" +
      new URLSearchParams({
        size: "300x300",
        data: url,
      }).toString()
    );
  }, [url]);

  useEffect(() => {
    let stop = false;

    (async () => {
      try {
        // carico device
        const rows = await getCryoList({ limit: 400 });
        if (stop) return;
        const found = rows.find((r) => String(r.id) === String(id));
        setDevice(found || null);
      } catch {
        // ok: stampo comunque
      }
    })();

    (async () => {
      try {
        const r = await fetch("/api/me.php", { credentials: "include" });
        const data = await r.json();
        if (!stop) setMe(data || null);
      } catch {
        // ok: azienda opzionale
      }
    })();

    return () => {
      stop = true;
    };
  }, [id]);

  const mac =
    device?.mac ?? device?.mac_address ?? device?.macAddress ?? macFromQs ?? "";

  const frigoName =
    device?.nome ||
    device?.name ||
    device?.label ||
    (mac ? `Frigo ${mac}` : `Frigo ${id}`);

  const reparto =
    device?.reparto || device?.zona || device?.location || "";

  // prova a prendere un nome azienda “buono”
  const azienda =
    me?.azienda_nome ||
    me?.azienda ||
    me?.company ||
    me?.nome_azienda ||
    "";

  const logoSrc = "/cryo-logo.png"; // oppure "/logo192.png"

  return (
    <div className="min-h-screen bg-white p-4 print:p-0">
      {/* CSS stampa A6 */}
      <style>{`
        @page { size: A6; margin: 6mm; }
        .sticker { width: 100%; max-width: 148mm; }
        .print-hide { display: block; }
        @media print {
          .print-hide { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="sticker mx-auto border rounded-2xl p-4 print:border-0 print:rounded-none">
        {/* Header */}
        <div className="flex items-center gap-3">
          <img
            src={logoSrc}
            alt="Cryo"
            className="w-12 h-12 object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="min-w-0">
            <div className="text-lg font-semibold leading-tight truncate">
              {frigoName}
            </div>
            {azienda ? (
              <div className="text-sm text-gray-600 truncate">{azienda}</div>
            ) : (
              <div className="text-sm text-gray-600">Monitoraggio temperature</div>
            )}
            {reparto ? (
              <div className="text-xs text-gray-500">Reparto: {reparto}</div>
            ) : null}
          </div>
        </div>

        {/* Corpo */}
        <div className="mt-4 grid grid-cols-2 gap-4 items-center">
          {/* QR */}
          <div className="border rounded-xl p-2">
            <img src={qrUrl} alt="QR inserimento temperatura" className="w-full h-auto" />
          </div>

          {/* Istruzioni */}
          <div className="text-sm">
            <div className="font-semibold mb-2">Come usare</div>
            <ol className="list-decimal ml-5 space-y-1 text-gray-700">
              <li>Scansiona il QR</li>
              <li>Inserisci la temperatura letta</li>
              <li>Premi “Salva”</li>
            </ol>

            <div className="mt-3 text-xs text-gray-500">
              La registrazione salva data/ora automatiche.
            </div>

            <div className="mt-3 text-xs text-gray-600">
              <div><span className="font-semibold">ID:</span> {id}</div>
              {mac ? <div><span className="font-semibold">MAC:</span> {mac}</div> : null}
            </div>
          </div>
        </div>

        {/* Footer link */}
        <div className="mt-3 text-[10px] text-gray-400 break-all">
          {url}
        </div>

        {/* pulsante stampa */}
        <button
          className="print-hide mt-4 w-full rounded-xl border px-3 py-2"
          onClick={() => window.print()}
        >
          Stampa adesivo A6
        </button>
      </div>
    </div>
  );
}
